import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Row, Col, Button, AutoComplete, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { setFilters } from "../customerLedgerSlice";
import { fetchCustomerLedger, searchCustomers } from "../customerLedgerThunk";
import RecordDateFilter from "../../../../components/filters/RecordDateFilter";
import SearchListAutoComplete from "../../../../components/SearchListAutoComplete";
import { useDefaultDateRange } from "../../../../hooks/useDefaultDateRange";
import CommonDateRangeFilter from "../../../../components/common/filters/CommonDateRangeFilter";

const CustomerLedgerFilters = ({ messageApi }) => {
  const dispatch = useDispatch();
  const { filters } = useSelector((state) => state.customerLedger);
  const { searchResults, loadingSearch } = useSelector(
    (state) => state.customerLedger
  );

  useDefaultDateRange(setFilters, filters, "today"); // 👈 defaults to today //default date using custom hook

  const handleSearchCustomers = useCallback(
    (text) => {
      dispatch(searchCustomers(text));
    },
    [dispatch]
  );

  useEffect(() => {
    // Auto-run report when all filters are set
    const hasAllFilters =
      filters.customerId &&
      filters.ledgerType &&
      filters.fromDate &&
      filters.toDate;
    if (hasAllFilters) {
      handleReport();
    }
  }, [
    filters.customerId,
    filters.ledgerType,
    filters.fromDate,
    filters.toDate,
  ]);

  const handleFilterChange = (field, value) => {
    dispatch(setFilters({ [field]: value }));
  };

  const handleReport = () => {
    if (!filters.customerId) {
      messageApi.warning("Select Customer");
      return;
    }

    if (!filters.ledgerType) {
      messageApi.warning("Select Ledger Type");
      return;
    }

    if (!filters.fromDate || !filters.toDate) {
      messageApi.warning("Select Date Range");
      return;
    }

    const payload = {
      customerId: filters.customerId,
      ledgerType: filters.ledgerType,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
    };

    dispatch(fetchCustomerLedger(payload)).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        messageApi.success("Ledger loaded ✅");
      } else {
        messageApi.error("Failed to fetch ledger ❌");
      }
    });
  };

  return (
    <Card className="mb-4 shadow-md rounded-xl">
      <Row gutter={16} style={{ alignItems: "center" }}>
        <Col xs={24} sm={12} md={12} lg={6}>
          <label>Search Customer</label>
          <SearchListAutoComplete
            entityType="customer"
            searchThunk={handleSearchCustomers}
            resultSelector={(state) => state.customerLedger.searchResults}
            loading={loadingSearch}
            onSelect={(obj) => {
              if (!obj) {
                // cleared
                handleFilterChange("customerId", null);
                handleFilterChange("customerName", "");
                return;
              }
              handleFilterChange("customerId", obj.acc_id);
              handleFilterChange("customerName", obj.acc_name);
                handleFilterChange("customerContact", obj.contact_no);  // ← add
  handleFilterChange("customerAddress", obj.address);     // ← add

            }}
            placeholder="Customer"
            showAddNew={false}
          />
        </Col>

        <Col xs={24} sm={12} md={12} lg={6}>
          <label>Ledger Type</label>
          <AutoComplete
            style={{ width: "100%" }}
            options={[
              { value: "with details", label: "With Details" },
              { value: "without details", label: "Without Details" },
            ]}
            value={filters.ledgerType}
            onSelect={(value) => handleFilterChange("ledgerType", value)}
          >
            <Input placeholder="Ledger Type" />
          </AutoComplete>
        </Col>

        <CommonDateRangeFilter
          filters={filters}
          onChange={(fromDate, toDate) => {
          handleFilterChange("fromDate", fromDate);
          handleFilterChange("toDate", toDate);
          }}
        />

        <Col
          xs={24}
          sm={12}
          md={12}
          lg={4}
          style={{ display: "flex", alignItems: "flex-end", height: 58 }}
        >
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleReport}
            block
             data-testid="btn-get-report"
          >
            Get Report
          </Button>
        </Col>
      </Row>
    </Card>
  );
};

export default CustomerLedgerFilters;
